import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { connectSocket, disconnectSocket, pauseSocket, resumeSocket } from "../lib/socket";
import { clearToken, getMutedConversationIds, syncNativeSettingsMirror } from "../lib/settings";
import {
  flashTaskbar,
  focusWindow,
  getMachineIdleSeconds,
  setBadgeUnread,
} from "../lib/tauri";
import { notify } from "../lib/notify";
import { installVisibilityPing, installSWMessageHandler } from "../lib/pwa";
import { initCapacitorShell, isCapacitor } from "../lib/capacitor";
import { reRegisterFcmIfGranted, setOpenConversationCallback } from "../lib/fcm";
import { playPling } from "../lib/sound";
import { drainOutbox, listOutbox } from "../lib/outbox";
import type { Conversation, Message, PresenceKind, Reaction } from "@swift/shared";
import Sidebar from "../components/Sidebar";
import MessageStream from "../components/MessageStream";
import Composer, { type ComposerHandle } from "../components/Composer";
import RightPanel from "../components/RightPanel";
import ConversationHeader from "../components/ConversationHeader";
import SharePicker from "../components/SharePicker";
import OutboxBanner from "../components/OutboxBanner";
import PinnedStrip from "../components/PinnedStrip";

type Props = { onLogout(): void };

export default function Main({ onLogout }: Props) {
  const me = useStore((s) => s.me);
  const conversations = useStore((s) => s.conversations);
  const activeId = useStore((s) => s.activeConversationId);
  const setActive = useStore((s) => s.setActiveConversation);
  const setConversations = useStore((s) => s.setConversations);
  const upsertConversation = useStore((s) => s.upsertConversation);
  const users = useStore((s) => s.users);
  const setUsers = useStore((s) => s.setUsers);
  const setConnection = useStore((s) => s.setConnection);
  const appendMessage = useStore((s) => s.appendMessage);
  const replaceMessage = useStore((s) => s.replaceMessage);
  const markMessageDeleted = useStore((s) => s.markMessageDeleted);
  const setPresence = useStore((s) => s.setPresence);
  const applyReactionAdded = useStore((s) => s.applyReactionAdded);
  const applyReactionRemoved = useStore((s) => s.applyReactionRemoved);
  const setTyping = useStore((s) => s.setTyping);
  const messagesByConv = useStore((s) => s.messagesByConv);

  useEffect(() => {
    void (async () => {
      const [u, c] = await Promise.all([api.listUsers(), api.listConversations()]);
      setUsers(u.users);
      setConversations(c.conversations);
      const current = useStore.getState().activeConversationId;
      if (current === null) {
        // Initiële selectie: eerste kanaal (DM's open je via de online-lijst).
        const firstChannel = c.conversations.find((conv) => conv.kind === "channel");
        if (firstChannel) setActive(firstChannel.id);
      }
    })();
  }, [setUsers, setConversations, setActive]);

  /* Eén keer per app-start slaan we het herladen over: de eerste `connect` valt samen
     met de gewone begin-lading hierboven. */
  const eersteVerbinding = useRef(true);

  useEffect(() => {
    const socket = connectSocket();
    setConnection("connecting");

    /*
     * Bijwerken wat er tijdens de stilte is gebeurd.
     *
     * WAAROM DIT MOET. Bij het naar de achtergrond gaan sluit de app de socket meteen —
     * dat is met opzet, want de server stuurt alleen een push naar wie géén open socket
     * heeft. Maar alles wat in die periode binnenkomt, komt daarmee ook nooit meer aan:
     * `message:new` is het enige wat berichten toevoegt, en MessageStream haalt een
     * gesprek alleen op als hij er nog geen berichten van heeft.
     *
     * Gevolg, zoals gemeld op 07-09-2026: je krijgt een melding op je telefoon, tikt erop,
     * komt in het gesprek — en het bericht staat er niet. Pas als je de app helemaal
     * opnieuw opent staat het er wel, want dan begint de begin-lading opnieuw.
     *
     * `connect` is hiervoor het juiste haakje: hij vuurt na een netwerkonderbreking én na
     * resumeSocket() bij het terugkomen uit de achtergrond. Eén plek dus, geen twee.
     */
    const HERLAAD_AANTAL = 50;
    const bijwerkenNaStilte = async () => {
      const [u, c] = await Promise.all([api.listUsers(), api.listConversations()]);
      setUsers(u.users);          // ook presence: presence:changed miste je net zo goed
      setConversations(c.conversations);

      const actief = useStore.getState().activeConversationId;
      if (actief === null) return;

      const bestaand = useStore.getState().messagesByConv[actief] ?? [];
      const nieuwsteBekend = bestaand.length > 0 ? bestaand[bestaand.length - 1]!.id : 0;
      const { messages: verse } = await api.listMessages(actief, undefined, HERLAAD_AANTAL);
      if (verse.length === 0) return;

      /* Zit er een gat tussen wat we hadden en wat we net ophaalden - er kwamen meer dan
         HERLAAD_AANTAL berichten binnen - dan is aanvullen niet genoeg en vervangen we de
         lijst. Anders vullen we alleen aan, zodat wie omhoog had gescrold niet naar
         beneden wordt geslingerd en oudere pagina's blijven staan. */
      if (bestaand.length === 0 || verse[0]!.id > nieuwsteBekend) {
        useStore.getState().setMessages(actief, verse, verse.length === HERLAAD_AANTAL);
      } else {
        for (const m of verse) {
          if (m.id > nieuwsteBekend) appendMessage(m);
        }
      }
    };

    socket.on("connect", () => {
      setConnection("connected");
      // Offline outbox legen zodra de socket weer staat — `connect` is
      // de meest betrouwbare reconnect-indicator (preciezer dan window
      // 'online', dat soms ten onrechte fired op een kapot AP).
      void drainOutbox(async () => {
        useStore.getState().setOutboxItems(await listOutbox());
      });

      if (eersteVerbinding.current) {
        eersteVerbinding.current = false;
        return;
      }
      void bijwerkenNaStilte().catch(() => undefined);
    });
    socket.on("disconnect", () => setConnection("disconnected"));
    socket.on("connect_error", () => setConnection("disconnected"));

    socket.on("message:new", async (payload: { message: Message }) => {
      const m = payload.message;
      appendMessage(m);
      if (me && m.sender_id !== me.id) {
        const focused = useStore.getState().windowFocused;
        const senderName =
          useStore.getState().users[m.sender_id]?.display_name ?? `Gebruiker #${m.sender_id}`;
        const convo = useStore.getState().conversations[m.conversation_id];
        const title =
          convo?.kind === "channel" && convo.name
            ? `#${convo.name} — ${senderName}`
            : senderName;
        const body = m.body.length > 140 ? m.body.slice(0, 140) + "…" : m.body;
        // Geluid + flash overslaan als 't bericht in de conversatie zit die
        // de gebruiker nu al voor zich heeft — je ziet 'm dan toch
        // letterlijk verschijnen. Wel pingen als het in een ander gesprek
        // valt (ook in een ander kanaal) of als het venster niet focused
        // is. Toast + taskbar-flash blijven aan unfocused vasthangen.
        const state = useStore.getState();
        const isActive = state.activeConversationId === m.conversation_id;
        const inViewActive = focused && isActive;
        // Gedempt gesprek → geen geluid/toast/flash, maar de badge blijft
        // tellen (appendMessage hierboven heeft 'm al opgeslagen). Server-
        // state (conversation.muted) overrult de lokale set.
        // @-mentions overrulen de mute: als ik (of @here/@channel) genoemd
        // ben, krijg je een pling en toast óók in een gedempt kanaal.
        // De server zet `mentions` op user-ids die expliciet (of via
        // broadcast) bedoeld zijn — daar zit ik in of niet. Defensieve
        // `?? []` voor het pad waar een pre-migratie server nog 'n
        // payload zonder mentions-veld stuurt; .includes op undefined
        // zou anders élk volgend bericht laten vastlopen.
        const mentioned = me ? (m.mentions ?? []).includes(me.id) : false;
        const muted =
          !mentioned &&
          ((convo?.muted ?? false) ||
            getMutedConversationIds().has(m.conversation_id));
        if (!inViewActive && !muted) {
          playPling();
        }
        if (!focused && !muted) {
          await notify(
            mentioned ? `🔔 ${title}` : title,
            mentioned ? `Je bent genoemd: ${body}` : body,
          );
          void flashTaskbar();
        }
      }
    });

    socket.on("message:edited", (payload: { message: Message }) => {
      replaceMessage(payload.message);
    });

    socket.on("message:deleted", (payload: { conversation_id: number; message_id: number }) => {
      markMessageDeleted(payload.conversation_id, payload.message_id);
    });

    socket.on(
      "presence:changed",
      (p: { user_id: number; status_kind: PresenceKind; status_text: string | null }) => {
        setPresence(p.user_id, p.status_kind, p.status_text);
        // Server zet ons bij socket-connect op 'online' en broadcast dat;
        // ook 'me' bijwerken zodat onze eigen stip/knop direct meebeweegt.
        const self = useStore.getState().me;
        if (self && p.user_id === self.id) {
          useStore.getState().setMe({
            ...self,
            status_kind: p.status_kind,
            status_text: p.status_text,
          });
        }
      },
    );

    socket.on("reaction:added", (p: { reaction: Reaction }) => {
      applyReactionAdded(p.reaction);
    });

    socket.on(
      "reaction:removed",
      (p: { message_id: number; user_id: number; emoji: string }) => {
        applyReactionRemoved(p.message_id, p.user_id, p.emoji);
      },
    );

    socket.on(
      "typing:start",
      (p: { conversation_id: number; user_id: number }) => setTyping(p.conversation_id, p.user_id, true),
    );
    socket.on(
      "typing:stop",
      (p: { conversation_id: number; user_id: number }) => setTyping(p.conversation_id, p.user_id, false),
    );

    socket.on(
      "read:advanced",
      (p: { conversation_id: number; user_id: number; last_read_message_id: number }) => {
        // Houd de eigen last-read pointer in sync (multi-device: ook bij
        // een andere sessie van jezelf). Andere gebruikers' read-state
        // tracken we (nog) niet.
        const self = useStore.getState().me;
        if (self && p.user_id === self.id) {
          useStore.getState().setLastRead(p.conversation_id, p.last_read_message_id);
        }
      },
    );

    socket.on("conversation:new", (p: { conversation: Conversation }) => {
      upsertConversation(p.conversation);
    });

    socket.on("conversation:updated", (p: { conversation: Conversation }) => {
      // upsertConversation werkt al als merge — naam/leden/broadcast worden
      // bijgewerkt zonder dat de positie in conversationOrder verandert.
      upsertConversation(p.conversation);
    });

    socket.on("conversation:removed", (p: { conversation_id: number }) => {
      useStore.getState().removeConversation(p.conversation_id);
    });

    socket.on("pin:added", (p: { conversation_id: number; message: Message }) => {
      useStore.getState().upsertPinned(p.conversation_id, p.message);
    });

    socket.on(
      "pin:removed",
      (p: { conversation_id: number; message_id: number }) => {
        useStore.getState().removePinned(p.conversation_id, p.message_id);
      },
    );

    return () => {
      disconnectSocket();
    };
  }, [me?.id]);

  // Unread badge in title. Zelfde bron als de Sidebar: de server-teller
  // (die ook berichten van vóór deze sessie meetelt), met lokaal tellen
  // als fallback voor een oudere server zonder unread_count-veld.
  useEffect(() => {
    let unread = 0;
    for (const conv of Object.values(conversations)) {
      if (typeof conv.unread_count === "number") {
        unread += conv.unread_count;
        continue;
      }
      const msgs = messagesByConv[conv.id] ?? [];
      const lastSeen = conv.last_read_message_id ?? 0;
      unread += msgs.filter((m) => m.id > lastSeen && m.sender_id !== me?.id).length;
    }
    void setBadgeUnread(unread);
  }, [conversations, messagesByConv, me?.id]);

  useEffect(() => {
    const onFocus = () => useStore.getState().setWindowFocused(true);
    const onBlur = () => useStore.getState().setWindowFocused(false);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // Offline outbox: bij startup de huidige queue in de store laden, en
  // bij elke 'online'-event opnieuw proberen. De socket-handler hierboven
  // doet hetzelfde bij reconnect — twee triggers omdat een browser
  // soms al online wordt vóór de socket terug staat.
  useEffect(() => {
    const sync = async () => {
      useStore.getState().setOutboxItems(await listOutbox());
    };
    void sync();
    const onOnline = () => {
      void drainOutbox(sync);
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  // PWA-presence: visibility-pings + SW-postMessage-handlers. Geen-op
  // onder Tauri.
  useEffect(() => {
    const offVis = installVisibilityPing();
    const offMsg = installSWMessageHandler({
      onOpenConversation: (id) => setActive(id),
    });
    return () => {
      offVis();
      offMsg();
    };
  }, [setActive]);

  // Capacitor (Android-APK): status-bar styling, hardware-back-button
  // (sluit overlays voor de app exitten), presence-ping bij resume, en
  // notification-tap → juiste conversatie openen. Het tap-pad loopt via
  // SharedPreferences: onze native SwiftMessagingService schrijft het
  // conversation_id, initCapacitorShell leest 'm bij cold-start en bij
  // elke app-resume.
  const closeLightbox = useStore((s) => s.closeLightbox);
  const closeSettings = useStore((s) => s.closeSettings);
  const composerRef = useRef<ComposerHandle>(null);
  const setPendingShare = useStore((s) => s.setPendingShare);
  useEffect(() => {
    if (!isCapacitor()) return;
    // Backup-pad: als Capacitor's plugin tóch een tap-event levert
    // (bv. bij een toekomstige notification-payload-route), pakken we 'm.
    setOpenConversationCallback((id) => setActive(id));

    // FCM-token her-registreren nu de permissie mogelijk al granted is.
    // Zonder dit stopten meldingen definitief zodra het token roteerde of
    // server-side werd opgeruimd (sessie-revoke, UNREGISTERED-cleanup).
    void reRegisterFcmIfGranted();

    // Server-URL + token beschikbaar maken voor de native meldingen-
    // service (preview-fetch); dekt installaties van vóór het mirror-
    // mechanisme die al ingelogd zijn.
    syncNativeSettingsMirror();

    // Vangnet naast Capacitor's appStateChange: op sommige Android-
    // versies is dat event flaky bij scherm-aan/uit. visibilitychange
    // vuurt in de WebView betrouwbaar; resumeSocket op een al verbonden
    // socket is een no-op, dus dubbel triggeren kan geen kwaad.
    const onVisible = () => {
      if (document.visibilityState === "visible") resumeSocket();
    };
    document.addEventListener("visibilitychange", onVisible);

    const cleanup = initCapacitorShell({
      onAppResume: () => {
        resumeSocket();
        void api.presencePing().catch(() => undefined);
        /*
         * Ook hier het FCM-token opnieuw aanmelden, niet alleen bij een koude
         * start. Android mag een token vervangen, en de server gooit een token
         * weg zodra FCM het UNREGISTERED noemt. Gebeurt dat terwijl de app al
         * dagen in de achtergrond hangt - wat de normale toestand is - dan
         * kwam er nooit meer een nieuw token binnen, want het proces wordt niet
         * opnieuw opgestart. Meldingen vielen dan stil zonder dat iemand iets
         * merkte, tot je de app een keer echt afsloot en opnieuw opende.
         *
         * Aanmelden is goedkoop en idempotent: staat de permissie niet aan, dan
         * doet het niets, en anders krijg je gewoon het token dat je al had.
         */
        void reRegisterFcmIfGranted();
      },
      onAppPause: () => {
        // Socket direct dicht: de server pusht alleen FCM naar users
        // zonder open socket, en de bevroren WebView-verbinding bleef
        // anders nog ~1 minuut "online" hangen — precies het venster
        // waarin testers meldingen misten.
        pauseSocket();
      },
      onOpenConversation: (id) => setActive(id),
      onShare: (content) => setPendingShare(content),
      onBack: () => {
        const state = useStore.getState();
        // Deel-kiezer heeft voorrang: hardware-back sluit 'm i.p.v. de app.
        if (state.pendingShare) {
          state.setPendingShare(null);
          return true;
        }
        if (state.mentionsOpen) {
          state.closeMentions();
          return true;
        }
        if (state.lightbox) {
          closeLightbox();
          return true;
        }
        if (state.settingsOpen) {
          closeSettings();
          return true;
        }
        if (state.mobileSidebarOpen) {
          state.setMobileSidebarOpen(false);
          return true;
        }
        return false;
      },
    });
    return () => {
      setOpenConversationCallback(null);
      document.removeEventListener("visibilitychange", onVisible);
      void cleanup.then((fn) => fn());
    };
  }, [setActive, closeLightbox, closeSettings, setPendingShare]);

  // Auto-away op basis van MACHINE-brede idle (GetLastInputInfo via de
  // Tauri-command get_idle_seconds). 5 min geen muis/toets op het hele
  // systeem → 'afwezig'. Eerste input weer → 'online' (zolang wij die
  // away zelf hebben gezet — handmatig op Bezet of Afwezig blijven we
  // af).
  useEffect(() => {
    if (!me) return;
    const IDLE_THRESHOLD = 5 * 60; // seconden
    const POLL_MS = 10_000;
    let auto = false;
    let stopped = false;

    const goAway = async () => {
      const cur = useStore.getState().me;
      if (!cur || cur.status_kind !== "online") return;
      try {
        const { user } = await api.updateMe({ status_kind: "away" });
        useStore.getState().setMe(user);
        auto = true;
      } catch {
        /* */
      }
    };

    const goOnline = async () => {
      if (!auto) return;
      auto = false;
      const cur = useStore.getState().me;
      if (!cur || cur.status_kind !== "away") return;
      try {
        const { user } = await api.updateMe({ status_kind: "online" });
        useStore.getState().setMe(user);
      } catch {
        /* */
      }
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const idle = await getMachineIdleSeconds();
      if (idle !== null) {
        if (idle >= IDLE_THRESHOLD) {
          await goAway();
        } else if (auto) {
          await goOnline();
        }
      }
      if (!stopped) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [me?.id]);

  async function doLogout() {
    try {
      await api.logout();
    } catch {
      /* */
    }
    clearToken();
    disconnectSocket();
    onLogout();
  }

  void focusWindow;
  const activeConv = activeId != null ? conversations[activeId] ?? null : null;

  // Composer wordt uitgeschakeld als de DM-partner offline is, of als
  // dit een aankondigingskanaal is en de gebruiker geen admin. Admins
  // krijgen de Composer wél en kunnen posten zoals altijd.
  let composerDisabled: string | null = null;
  if (activeConv && activeConv.kind === "dm") {
    const otherId = activeConv.member_ids.find((id) => id !== me?.id);
    const other = otherId !== undefined ? users[otherId] : undefined;
    if (other && other.status_kind === "offline") {
      composerDisabled = `${other.display_name} is offline — je kan pas weer typen als ${other.display_name} terugkomt.`;
    }
  } else if (activeConv?.is_announcement && !me?.is_admin) {
    composerDisabled =
      "📢 Aankondigingskanaal — alleen beheerders kunnen hier posten. Reageren met emoji mag.";
  }

  const [isDragging, setIsDragging] = useState(false);
  // dragenter/dragleave triggeren ook bij kind-elementen; we tellen
  // diepte zodat de overlay alleen verdwijnt als de cursor de hele pane
  // verlaat (counter == 0).
  const dragDepth = useRef(0);

  function hasFiles(e: React.DragEvent): boolean {
    return Array.from(e.dataTransfer.types).includes("Files");
  }

  function onDragEnter(e: React.DragEvent<HTMLElement>) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  }
  function onDragOver(e: React.DragEvent<HTMLElement>) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = composerDisabled ? "none" : "copy";
  }
  function onDragLeave(e: React.DragEvent<HTMLElement>) {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  }
  function onDrop(e: React.DragEvent<HTMLElement>) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (composerDisabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) composerRef.current?.addFiles(files);
  }

  const mobileSidebarOpen = useStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useStore((s) => s.setMobileSidebarOpen);
  const pendingShare = useStore((s) => s.pendingShare);

  const mobileSidebarStyle = isCapacitor()
    ? {
        // Inline zodat Tailwind's fixed/inset-y utilities dit niet kunnen overrulen.
        // Ruim genoeg voor Android statusbar + wat ademruimte boven het zoekveld.
        top: "32px",
        bottom: "16px",
        height: "auto",
      }
    : undefined;

  return (
    <div className="relative flex h-full">
      {/* Sidebar — op desktop (md+) statisch links; op mobile een
          drawer over de hele inhoud. */}
      <aside
        style={mobileSidebarStyle}
        className={`mobile-sidebar fixed inset-y-0 left-0 z-30 flex w-64 shrink-0 transform flex-col border-r border-slate-700 bg-slate-800 transition-transform md:static md:translate-x-0 ${
          mobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <Sidebar active={activeId} onSelect={setActive} onLogout={doLogout} />
      </aside>
      {/* Klik-buiten-overlay alleen op mobile, alleen als drawer open. */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <main
        className="relative flex min-w-0 flex-1 flex-col"
        onDragEnter={activeConv ? onDragEnter : undefined}
        onDragOver={activeConv ? onDragOver : undefined}
        onDragLeave={activeConv ? onDragLeave : undefined}
        onDrop={activeConv ? onDrop : undefined}
      >
        {/* Hamburger-balk — alleen op mobile (md:hidden). */}
        <div className="flex h-10 shrink-0 items-center gap-2 border-b border-slate-700 bg-slate-900 px-2 md:hidden">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded text-slate-300 hover:bg-slate-700"
            aria-label="Menu openen"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span className="truncate text-sm text-slate-300">
            {activeConv
              ? activeConv.kind === "channel"
                ? `#${activeConv.name ?? ""}`
                : (() => {
                    const otherId = activeConv.member_ids.find((id) => id !== me?.id);
                    const other = otherId != null ? users[otherId] : undefined;
                    return other?.display_name ?? "Gesprek";
                  })()
              : "Swift"}
          </span>
        </div>
        {activeConv ? (
          <>
            <ConversationHeader conversation={activeConv} />
            <PinnedStrip conversation={activeConv} />
            <MessageStream conversation={activeConv} />
            <Composer
              ref={composerRef}
              conversation={activeConv}
              disabledReason={composerDisabled ?? undefined}
            />
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-slate-500">
            Kies links een kanaal of klik op een online collega.
          </div>
        )}
        {isDragging && activeConv && (
          <div
            className={`pointer-events-none absolute inset-0 z-20 flex items-center justify-center backdrop-blur-sm ${
              composerDisabled
                ? "bg-red-950/40 border-2 border-dashed border-red-600"
                : "bg-accent/15 border-2 border-dashed border-accent"
            }`}
          >
            <div className="flex flex-col items-center gap-2 text-slate-100">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div className="text-sm font-medium">
                {composerDisabled
                  ? "Kan niet uploaden — gebruiker is offline"
                  : "Laat los om te uploaden"}
              </div>
            </div>
          </div>
        )}
      </main>
      {/* RightPanel alleen op desktop — op mobile zou 't te druk worden;
          de info zit ook in de Sidebar-drawer. */}
      {activeConv && (
        <div className="hidden md:block">
          <RightPanel conversation={activeConv} />
        </div>
      )}
      {/* Deel-kiezer — full-screen overlay zodra er inhoud naar Swift
          gedeeld is (Android share-sheet). */}
      {pendingShare && <SharePicker />}
    </div>
  );
}
